"use client";

import { type Icon, IconChevronRight, IconChevronDown } from "@tabler/icons-react";
import * as React from "react";
import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useNavigation } from "@/contexts/navigation-context";
import { cn } from "@/lib/shared/utils";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import { NavigationItem } from "@/types/menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export function NavSecondary({
  items,
  ...props
}: {
  items: NavigationItem[];
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const { userRole } = useNavigation();
  const pathname = usePathname();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Check if an item or any of its children is active
  const isItemActive = (item: NavigationItem): boolean => {
    if (item.url && item.url !== "#") {
      if (pathname === item.url || pathname.startsWith(item.url + "/")) {
        return true;
      }
    }
    // Check children
    if (item.children) {
      return item.children.some(child => isItemActive(child));
    }
    return false;
  };

  // Auto-expand items that have active children
  React.useEffect(() => {
    const newExpanded = new Set<string>();
    items.forEach(item => {
      if (item.children && item.children.some(child => isItemActive(child))) {
        newExpanded.add(item.id || item.title);
      }
    });
    if (newExpanded.size > 0) {
      setExpandedItems(prev => new Set([...prev, ...newExpanded]));
    }
  }, [pathname]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  return (
    <SidebarMenu>
      {items.map((item) => {
        const isDisabled = item.disabled === true;
        const hasChildren = item.children && item.children.length > 0;
        const IconComponent = item.icon as Icon;
        const isActive = isItemActive(item);
        const itemId = item.id || item.title;
        const isExpanded = expandedItems.has(itemId);

        // Items with children use Collapsible
        if (hasChildren) {
          return (
            <Collapsible
              key={itemId}
              open={isExpanded}
              onOpenChange={() => toggleExpanded(itemId)}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={item.title}
                    isActive={isActive && !isExpanded}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center w-full">
                      {IconComponent && <IconComponent className="transition-all duration-200 flex-shrink-0 h-4 w-4" />}
                      <span className="flex-1 transition-all duration-200 group-data-[collapsible=icon]:hidden">
                        {item.title}
                      </span>
                      {isExpanded ? (
                        <IconChevronDown className="h-4 w-4 text-muted-foreground transition-all duration-200 group-data-[collapsible=icon]:hidden" />
                      ) : (
                        <IconChevronRight className="h-4 w-4 text-muted-foreground transition-all duration-200 group-data-[collapsible=icon]:hidden" />
                      )}
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.children!.map((child) => {
                      // Skip header items
                      if (child.type === "header") {
                        return (
                          <div
                            key={child.id || child.title}
                            className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider mt-2 first:mt-0"
                          >
                            {child.title}
                          </div>
                        );
                      }
                      const ChildIcon = child.icon as Icon;
                      const isChildActive = !!(child.url && (pathname === child.url || pathname.startsWith(child.url + "/")));
                      return (
                        <SidebarMenuSubItem key={child.id || child.title}>
                          <SidebarMenuSubButton asChild isActive={isChildActive}>
                            <Link href={child.url || "#"}>
                              {ChildIcon && <ChildIcon className="h-4 w-4" />}
                              <span>{child.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        }

        // Regular items without children
        return (
          <SidebarMenuItem key={itemId}>
            <SidebarMenuButton
              asChild={!isDisabled}
              tooltip={isDisabled ? `${item.title} (Coming Soon)` : item.title}
              isActive={isActive}
              className={cn(
                isDisabled && "cursor-not-allowed opacity-50 text-muted-foreground pointer-events-none"
              )}
            >
              {!isDisabled ? (
                <Link href={item.url} className="flex items-center w-full">
                  {IconComponent && <IconComponent className="transition-all duration-200 flex-shrink-0 h-4 w-4" />}
                  <span className="flex-1 transition-all duration-200 group-data-[collapsible=icon]:hidden">
                    {item.title}
                  </span>
                </Link>
              ) : (
                <div className="flex items-center w-full">
                  {IconComponent && <IconComponent className="transition-all duration-200 flex-shrink-0 h-4 w-4" />}
                  <span className="flex-1 transition-all duration-200 group-data-[collapsible=icon]:hidden">
                    {item.title}
                  </span>
                </div>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}
